import { useState } from 'react'
import { Modal } from '@/components/ui/Modal'
import { setWordStrength } from '@/api/words'
import { STRENGTH_LEVELS } from '@/utils/strengthLevels'
import { cn } from '@/utils/cn'
import type { WordVocabularyEntry } from '@/types/vocabulary'
import type { StrengthLevel } from '@/types/word'
import { focusRing, quietFill } from '@/components/ui/buttonStyles'

interface EditStrengthModalProps {
  entry: WordVocabularyEntry
  languageId: number
  onClose: () => void
  onSaved: () => void
}

const STATUS_LABELS: Record<string, string> = {
  new: 'New', learning: 'Learning', review: 'Review', mastered: 'Mastered',
}

/**
 * Sets a tracked word's schedule by hand — say you already know it, or send it
 * back to the start when a strength was set too high.
 */
export function EditStrengthModal({ entry, languageId, onClose, onSaved }: EditStrengthModalProps) {
  const [saving, setSaving] = useState<StrengthLevel | null>(null)
  const [failed, setFailed] = useState(false)

  function handleSelect(level: StrengthLevel) {
    setSaving(level)
    setFailed(false)
    setWordStrength(languageId, entry.word_id, level)
      .then(() => {
        onSaved()
        onClose()
      })
      .catch(() => {
        setSaving(null)
        setFailed(true)
      })
  }

  return (
    <Modal open onClose={onClose} title="Edit strength">
      <p className="text-sm text-muted font-light leading-relaxed">
        <span className="font-serif text-base text-heading">{entry.lemma}</span> is
        currently <span className="text-body">{STATUS_LABELS[entry.status] ?? entry.status}</span>.
        Choosing a strength replaces its review progress.
      </p>

      <ul className="flex flex-col gap-2 mt-5">
        {STRENGTH_LEVELS.map(({ level, label, hint }) => (
          <li key={level}>
            <button
              type="button"
              onClick={() => handleSelect(level)}
              disabled={saving !== null}
              className={cn(
                'w-full text-left rounded-lg px-4 py-3 min-h-11',
                'transition-colors duration-200 disabled:opacity-50',
                quietFill,
                focusRing,
              )}
            >
              <span className="block text-sm font-normal">
                {saving === level ? 'Saving…' : label}
              </span>
              <span className="block text-xs font-light opacity-80 mt-0.5">{hint}</span>
            </button>
          </li>
        ))}
      </ul>

      {failed && (
        <p role="alert" className="text-xs text-muted font-light mt-4">
          Could not update the strength. Try again.
        </p>
      )}
    </Modal>
  )
}
